import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { useAuth } from '../../../context/auth-context'
import { Table } from 'antd'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { useDateTime } from '../../../shared/useDateTime'
import { useCompanySettingsStore } from '../CompanySettingsStore'
import { ReviewerAndEmployee, ReviewerOfEmployeeType } from '../../../types'
import { DeleteReviewerModal } from './DeleteReviewerModal'
import { getReviewerTableColumns } from './reviewerTableColumns'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { PerTag } from '../../../components/custom_components/Tag'
import { ColumnProps } from 'antd/lib/table'

interface Props {
  tableData: ReviewerAndEmployee[]
  canEdit: boolean
  isHrAdmin: boolean
}

export const ReviewerTable: FC<Props> = ({ tableData, canEdit, isHrAdmin }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [selected] = useCompanySettingsStore((state) => [state.selected], shallow)
  const [relationToDelete, setRelationToDelete] = useState<ReviewerAndEmployee | null>(null)

  const { formatDateShort } = useDateTime(user)
  const { tableActionButtonsRowWidth } = tableActionButtons()
  const tagColor = (type: ReviewerOfEmployeeType) => (type === ReviewerOfEmployeeType.Internal ? 'cyan' : 'gold')

  const columns = [
    ...getReviewerTableColumns({
      t,
      user,
      formatDateShort,
    }),
    {
      key: 'reviewer-type',
      title: t('companySettingsView.reviewerTab.table.type.heading'),
      className: 'minWidth-column-m',
      sorter: (fromA: ReviewerAndEmployee, fromB: ReviewerAndEmployee) => {
        return fromA.type && fromB.type && fromA.type.localeCompare(fromB.type)
      },
      render: (tableData: ReviewerAndEmployee) => (
        <PerTag color={tagColor(tableData.type)}>{t(`companySettingsView.reviewerTab.table.type.${tableData.type}`)}</PerTag>
      ),
    },
    {
      key: 'reviewer-actions',
      title: t('companySettingsView.reviewerTab.table.actions.heading'),
      width: tableActionButtonsRowWidth(1),
      render: (tableData: ReviewerAndEmployee) => (
        <>
          {tableData.type === ReviewerOfEmployeeType.External && !isHrAdmin ? (
            <PerButtonCircle
              icon="info_stroke"
              color="secondary"
              tooltip={t('companySettingsView.reviewerTab.table.actions.infoBtnTooltip')}
            />
          ) : (
            <PerButtonCircle
              variant="pastel"
              color="error"
              icon="delete_fill"
              tooltip={t('companySettingsView.reviewerTab.table.actions.deleteBtnTooltip')}
              onClick={() => setRelationToDelete(tableData)}
            />
          )}
        </>
      ),
    },
  ]

  const filterActionsRow = (column: ColumnProps<ReviewerAndEmployee>) => canEdit || column.key !== 'reviewer-actions'
  const tColumns = columns.filter((c) => filterActionsRow(c))
  const tData = tableData.map((employee) => ({ ...employee, key: employee.id }))

  return (
    <>
      <DeleteReviewerModal onClose={() => setRelationToDelete(null)} relationToDelete={relationToDelete} companyId={selected.companyId} />
      <Table
        className="light small viewWithTabsAndInfoBox scroll-compensation"
        rowClassName={() => 'minHeight-row'}
        dataSource={tData}
        columns={tColumns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: <PerEmpty icon="employeesNo_stroke" heading={t('companySettingsView.reviewerTab.table.empty.heading')} />,
        }}
      />
    </>
  )
}
